import React from "react";
import Switch from "@mui/material/Switch";
import Skeleton from "react-loading-skeleton";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select, { SingleValue } from "react-select";
import TextAreaCustom from "../../../../../common/TextAreaCustom";
import useCompany from "../../../../../store/ReactQueries/general/company/useCompany";
import { FieldErrors } from "../../../../../domain/helpers/Errors";
import { BootstrapInput } from "../../../../../common/GlobalFunctions";
import { useDarkModeStore } from "../../../../../store/useDarkModeStore";
import { darkModeBackgroudInputs } from "../../../../../utils/constants";
import { useLanguageStore } from "../../../../../store/useLanguageStore";
import { CustomerInterface } from "../../../../../models/TicketModule/Customer";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

export type CustomerTypeInputs = "text" | "multitext" | "select" | "checkbox" | "date" | "photo" | "number" | "phone";

interface CustomizedInputsStyledProps<T> {
  name: string;
  label: string;
  view: boolean;
  loading: boolean;
  errors: FieldErrors;
  form: CustomerInterface;
  type: CustomerTypeInputs;
  setForm: React.Dispatch<React.SetStateAction<CustomerInterface>>;
}

const CustomizedInputsStyled = <T,>({
  label,
  type,
  name,
  form,
  errors,
  view,
  loading,
  setForm,
}: CustomizedInputsStyledProps<T>) => {
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();

  const { data: companies } = useCompany(0, 0);

  const handleText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    delete errors?.[name];
    setForm({ ...form, [name]: value });
  };

  const handleChangeBool = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    setForm((prevForm) => ({
      ...prevForm,
      [fieldName]: e.target.checked,
    }));
  };

  const renderData: any[] = React.useMemo(() => {
    switch (name) {
      case "companyId":
        return companies ?? [];
      default:
        return [];
    }
  }, [name, companies]);

  const handleMultiSelect = (
    selectedOption: SingleValue<{
      label: any;
      value: string;
    }>
  ) => {
    delete errors?.[name];
    delete errors?.[name.slice(0, -2)];
    if (!selectedOption) {
      setForm({
        ...form,
        [name]: "",
        [name.slice(0, -2)]: { title: "" },
      });
      return;
    }

    let { ...upForm } = form;
    upForm[name] = selectedOption.value;
    upForm[name.slice(0, -2)] = { title: selectedOption.label };

    setForm(upForm);
  };

  const renderInput = () => {
    switch (type) {
      case "text":
        return (
          <BootstrapInput
            darkmode={darkMode ? darkMode.toString() : "false"}
            value={form?.[name] ?? ""}
            id={name}
            name={name}
            onChange={handleText}
            sx={errors?.[name] ? { border: "1px solid #DC3545", borderRadius: "4px" } : {}}
            readOnly={view}
          />
        );
      case "phone":
        return (
          <div
            className={`mt-4 phone-field ${darkMode ? "dark-mode-input" : ""} ${errors?.[name] ? "has-error" : ""}`}
            style={view ? { pointerEvents: "none" } : {}}
          >
            <PhoneInput
              international
              defaultCountry="XK"
              value={form?.[name] ?? ""}
              onChange={(val) => {
                if (!view) setForm((p: any) => ({ ...p, [name]: val || "" }));
              }}
              placeholder="+383"
            />
          </div>
        );

      case "number":
        return (
          <BootstrapInput
            darkmode={darkMode ? darkMode.toString() : "false"}
            value={form?.[name] ?? ""}
            id={name}
            type="number"
            name={name}
            onChange={handleText}
            sx={errors?.[name] ? { border: "1px solid #DC3545", borderRadius: "4px" } : {}}
            readOnly={view}
          />
        );
      case "multitext":
        return (
          <TextAreaCustom errors={errors} form={form} name={name} setForm={setForm} view={view} className="mt-4" />
        );
      case "checkbox":
        return (
          <Switch
            id={label}
            checked={form?.[name] ?? false}
            className="mt-4 ms-2"
            disabled={view}
            onChange={(e) => handleChangeBool(e, name)}
          />
        );
      case "select":
        return (
          <Select
            value={{ value: form?.[name], label: renderData?.find((item) => item.id === form?.[name])?.title ?? "" }}
            className={`mt-4 ${darkMode && "dark-mode-input"}`}
            onChange={handleMultiSelect}
            placeholder="Zgjedh"
            options={renderData?.map(({ id, title }) => ({
              value: id ?? "",
              label: title ?? "",
            }))}
            isClearable
            isDisabled={view}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 11 }),
              container: (provided) => ({
                ...provided,
                position: "relative",
                zIndex: 11,
              }),
              singleValue: (provided) => ({
                ...provided,
                color: darkMode ? "white" : "black",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 11 }),
              control: (provided) => ({
                ...provided,
                backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
                borderColor: darkMode ? darkModeBackgroudInputs : "initial",
              }),
            }}
            menuPlacement="top"
            menuPortalTarget={document.body}
          />
        );
      default:
        return null;
    }
  };

  return loading ? (
    <div className="d-flex flex-column">
      <Skeleton className="mb-2" style={{ width: 50, height: 17 }} />
      <Skeleton style={{ width: "100%", height: 40 }} />
    </div>
  ) : (
    <FormControl
      variant="standard"
      className={`${(type === "multitext" || name === "dateTime") && "modalMergeTwoRows"}`}
    >
      <InputLabel
        shrink
        htmlFor={name}
        sx={errors?.[name] ? { color: "#DC3545" } : { color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)" }}
      >
        {language[label]}
      </InputLabel>
      {renderInput()}
      {errors?.[name] && (
        <p className="text-danger mb-0" style={{ fontSize: "12px" }}>
          {errors?.[name]}
        </p>
      )}
    </FormControl>
  );
};

export default CustomizedInputsStyled;
